import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import { Loader2, ArrowUpRight, ShieldCheck } from "lucide-react";

/* Meta Embedded Signup button — WhatsApp Phase C.
 *
 * Renders one CTA. On click:
 *   1. Ensure the Facebook JS SDK is loaded.
 *   2. Register a `postMessage` listener for the `WA_EMBEDDED_SIGNUP` event
 *      Meta posts back with the WABA ID + phone number ID.
 *   3. Fire `FB.login({config_id, response_type: 'code'})` which pops the
 *      Meta consent popup.
 *   4. On success POST {code, waba_id, phone_number_id} to our backend.
 *   5. Backend exchanges code → business token, validates, persists.
 *
 * The App Secret NEVER touches the browser — only App ID + Config ID do.
 */

const APP_ID      = process.env.REACT_APP_META_APP_ID;
const CONFIG_ID   = process.env.REACT_APP_META_CONFIG_ID;
const GRAPH_VER   = process.env.REACT_APP_META_GRAPH_VERSION || "v20.0";

function loadFacebookSdk() {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") return reject(new Error("no window"));
    if (window.FB) return resolve(window.FB);

    // Meta's SDK reads this init hook when the script finishes loading.
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: APP_ID, autoLogAppEvents: true, xfbml: true, version: GRAPH_VER,
      });
      resolve(window.FB);
    };
    if (document.getElementById("facebook-jssdk")) return; // pending
    const s = document.createElement("script");
    s.id = "facebook-jssdk";
    s.async = true; s.defer = true; s.crossOrigin = "anonymous";
    s.src = "https://connect.facebook.net/en_US/sdk.js";
    s.onerror = () => reject(new Error("Failed to load Facebook SDK"));
    document.body.appendChild(s);
  });
}

export default function MetaEmbeddedSignupButton({ onConnected, className = "" }) {
  const [busy, setBusy] = useState(false);
  const signupDataRef = useRef({ waba_id: null, phone_number_id: null });

  // Register message listener once — Meta will postMessage from its popup
  // with `WA_EMBEDDED_SIGNUP` before FB.login's callback fires.
  useEffect(() => {
    const handler = (event) => {
      const trusted = ["https://www.facebook.com", "https://web.facebook.com"];
      if (!trusted.includes(event.origin)) return;
      let payload;
      try { payload = typeof event.data === "string" ? JSON.parse(event.data) : event.data; }
      catch { return; }
      if (payload?.type !== "WA_EMBEDDED_SIGNUP") return;
      if (payload?.event === "FINISH" || payload?.event === "FINISH_ONLY_WABA") {
        signupDataRef.current = {
          waba_id:         payload.data?.waba_id         || null,
          phone_number_id: payload.data?.phone_number_id || null,
        };
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const connect = async () => {
    if (!APP_ID || !CONFIG_ID) {
      toast.error("Meta App ID / Config ID not configured. Contact support.");
      return;
    }
    setBusy(true);
    try {
      const FB = await loadFacebookSdk();
      signupDataRef.current = { waba_id: null, phone_number_id: null };

      FB.login(async (response) => {
        try {
          const code = response?.authResponse?.code;
          const { waba_id, phone_number_id } = signupDataRef.current;
          if (!code) {
            toast.error("Meta signup was cancelled.");
            return;
          }
          if (!waba_id || !phone_number_id) {
            toast.error("Meta didn't return a WABA / phone. Please retry.");
            return;
          }
          const { data } = await api.post("/whatsapp/embedded-signup/exchange", {
            code, waba_id, phone_number_id,
          });
          toast.success("WhatsApp Business connected!");
          onConnected?.(data.whatsapp_meta);
        } catch (e) {
          toast.error(apiError(e, "Meta connection failed"));
        } finally { setBusy(false); }
      }, {
        config_id: CONFIG_ID,
        response_type: "code",
        override_default_response_type: true,
        extras: { setup: {}, featureType: "whatsapp_business_app_onboarding" },
      });
    } catch (e) {
      toast.error(e.message || "Could not load Facebook SDK");
      setBusy(false);
    }
  };

  return (
    <button onClick={connect} disabled={busy} data-testid="meta-embedded-signup-btn"
      className={`inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1EBE5A] text-white px-5 py-2.5 text-sm font-semibold shadow-sm transition-colors disabled:opacity-60 ${className}`}>
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
      {busy ? "Connecting…" : "Connect WhatsApp Business"}
      {!busy && <ArrowUpRight className="w-4 h-4" />}
    </button>
  );
}
